/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, Suspense, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';

import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronLeftLargeIcon from '@atlaskit/icon/core/chevron-left';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, Inline, Stack } from '@atlaskit/primitives/compiled';
import { AgentVerified } from '@atlaskit/rovo-agent-components/agent-verified';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentBanner } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { AgentStarCount } from '@atlaskit/rovo-agent-components/ui/agent-profile-info/AgentStarCount';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { AgentProfileInfo } from '@atlaskit/rovo-agent-components/ui/AgentProfileInfo';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { AnalyticsContextWithSourceAndProperties } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type AIMateAgentAttributeType } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../../../common/constants/private.ts';
import { isForgeAgent, type AgentRendererProps } from '../../../../common/types.ts';
import { AgentConversationStartersWithAnalytics } from '../../../../common/ui/agent-conversation-starters/index.tsx';
import {
	AgentDropdownMenu,
	ChatToAgentButton,
} from '../../../../common/ui/agent-interactions/index.tsx';
import { AgentCreatorDataComponent } from '../../../../common/ui/creator/index.tsx';
import { getConversationStarters } from '../../../../common/utils/conversation-starters/index.ts';
import {
	type AgentDetailsWithAggData,
	useAgentDetailsController,
} from '../../../../controllers/agent-details/index.ts';
import { useAgentUrlActions } from '../../../../controllers/agent-url-actions/index.tsx';
import { useAgentsPaginatedActionsWithOptimistic } from '../../../../controllers/agents/index.tsx';
import { isAutodevTemplateAgent as isAutodevTemplateAgentUtil } from '../../../../controllers/browse-agents-ui/utils.ts';
import { useTenantDataController } from '../../../../controllers/tenant-data/index.tsx';

import { type viewAgent_activationQuery } from './__generated__/viewAgent_activationQuery.graphql.ts';
import type { viewAgent_agentDetailsQuery } from './__generated__/viewAgent_agentDetailsQuery.graphql.ts';
import type { viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef$key } from './__generated__/viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef.graphql.ts';
import type { viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef$key } from './__generated__/viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef.graphql.ts';
import type { viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef$key } from './__generated__/viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef.graphql.ts';
import { AgentAboutCard } from './agent-about/index.tsx';
import { AgentBehaviour } from './behaviour/index.tsx';
import { AgentInstructions } from './instructions/index.tsx';
import messages from './messages.ts';
import { ScenariosSection } from './scenarios-section/index.tsx';
import { ViewAgentDetailSkeleton } from './view-agent-skeleton/index.tsx';

const styles = cssMap({
	modalBody: {
		paddingBottom: token('space.300'),
		height: '100%',
		gridTemplateColumns: '1fr 2fr',
	},

	banner: {
		marginBottom: token('space.500'),
		marginInline: token('space.negative.400'),
	},

	avatar: {
		marginBottom: token('space.300'),
	},

	placeholderContainer: {
		marginInline: token('space.200'),
		marginBlock: token('space.200'),
	},
});

const RightSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
	return (
		<Stack space="space.200" alignBlock="start">
			<Heading size="small" as="h2">
				{title}
			</Heading>
			{children}
		</Stack>
	);
};

type ViewAgentModalInternalProps = {
	agentAri: string;
	agentDetailsData: AgentDetailsWithAggData;
	onClose: () => void;
	onGoBack?: () => void;
	onClickChat: (agent: Agent) => void;
	handleClickConversationStarter: (agent: Agent, conversationStarter: ConversationStarter) => void;
	onDeleteAgent: (agent: Agent, source: string) => void;
	agentRef: viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef$key | null;
	scenarioListRef: viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef$key | null;
	userPermissionsRef: viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef$key | null;
	enableJiraAgentFields?: boolean;
	renderAgentProfileCta?: (props: AgentRendererProps) => React.ReactNode;
	renderAgentProfileAdditionalContent?: (props: AgentRendererProps) => React.ReactNode;
} & AssistanceServiceInputParam;

const ViewAgentModalInternal = ({
	agentAri,
	agentDetailsData,
	onClose,
	onGoBack,
	onClickChat,
	handleClickConversationStarter,
	onDeleteAgent,
	assistanceServiceParams,
	agentRef,
	scenarioListRef,
	userPermissionsRef,
	renderAgentProfileCta,
	renderAgentProfileAdditionalContent,
}: ViewAgentModalInternalProps) => {
	di(AgentBanner, AgentAvatar);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const { setFavouriteAgent } = useAgentsPaginatedActionsWithOptimistic({
		assistanceServiceParams,
	});

	const userDefinedConversationStarters = getConversationStarters(agentDetailsData);
	const { onEditAgent, onCopyAgent, onDuplicateAgent, onViewAgentFullProfile } = useAgentUrlActions(
		{
			siteId: assistanceServiceParams.siteId,
			source: 'viewAgent',
			xpcBridge: 'rovoAgents-agentProfileModal',
		},
	);
	const { data: tenantData } = useTenantDataController(assistanceServiceParams.siteId);

	const agentFragment =
		useFragment<viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef$key>(
			graphql`
				fragment viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef on AgentStudioAssistant
				@argumentDefinitions(
					enableJiraAgentFields: { type: "Boolean!" }
					enableStudioGSNAndScenarioRefresh: { type: "Boolean!" }
					cloudId: { type: "ID!" }
				) {
					permissions @include(if: $enableJiraAgentFields) {
						canEdit
					}
					jiraAssignability(cloudId: $cloudId)
						@optIn(to: "JiraAssignableAgents")
						@include(if: $enableJiraAgentFields) {
						isAssignable
					}
					instructions @include(if: $enableStudioGSNAndScenarioRefresh)
					agentSchemaNumber
					...creator_AtlassianConversationAssistantAgent_AgentCreatorDataComponent
					...agentVerified_AtlaskitRovoAgentComponents
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_agentRef
				}
			`,
			agentRef,
		);

	const scenarioListFragment =
		useFragment<viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef$key>(
			graphql`
				fragment viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef on AgentStudioScenariosResult {
					...agentAbout_AtlassianConversationAssistantAgent_AgentAboutCard
					...scenariosSection_AtlassianConversationAssistantAgent
				}
			`,
			scenarioListRef,
		);

	const userPermissionsFragment =
		useFragment<viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef$key>(
			graphql`
				fragment viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef on AtlassianStudioUserProductPermissions {
					# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
					...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_userPermissionsRef
				}
			`,
			userPermissionsRef,
		);

	const isAutodevTemplateAgent = isAutodevTemplateAgentUtil;
	const isAgentV2 = agentFragment?.agentSchemaNumber === 2;

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'viewAgentModal',
		});
	}, [sendAnalyticsEvent, agentDetailsData.id]);

	const handleGoBack = (analyticsEvent: UIAnalyticsEvent) => {
		onGoBack?.();
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentBackButton',
			}),
		);
	};

	const handleClickChat = (analyticsEvent: UIAnalyticsEvent) => {
		onClickChat(agentDetailsData);
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentChatToAgentButton',
			}),
		);
	};

	const handleFavouriteAgent = async (agentId: string) => {
		await setFavouriteAgent(agentId, !agentDetailsData.favourite);

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'viewAgentFavoriteAgentButton',
			attributes: {
				isFavorited: !agentDetailsData.favourite,
			},
		});
	};

	const handleClickCreatorLink = () => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'viewAgentCreatorLink',
		});
	};

	const handleCopy = (value: string) => {
		navigator.clipboard.writeText(value.toString());

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'viewAgentCopyInstructionsButton',
		});
	};

	const isCurrentAgentForgeAgent = fg('rovo_agent_support_a2a_avatar')
		? isForgeAgentByCreatorType(agentDetailsData.creator_type as AgentCreatorType)
		: isForgeAgent(agentDetailsData);

	const hasCustomScenarios = fg('jira_improve_agent_profile_for_a2a')
		? Boolean(agentDetailsData.scenarios?.length && agentDetailsData.scenarios.length > 1)
		: agentDetailsData.scenarios?.length && agentDetailsData.scenarios.length > 1;

	const agentInstructions = useMemo(() => {
		if (!fg('studio-gsn-and-scenario-refresh')) {
			return undefined;
		}
		if (isAgentV2) {
			return agentFragment?.instructions ?? undefined;
		}
		return agentDetailsData.scenarios?.find((scenario) => scenario.isDefault)?.instructions;
	}, [agentDetailsData.scenarios, isAgentV2, agentFragment?.instructions]);

	const showBehaviourSection = fg('jira_improve_agent_profile_for_a2a')
		? Boolean(!isAgentV2 && !isCurrentAgentForgeAgent)
		: !isAgentV2;

	return (
		<Fragment>
			<ModalHeader hasCloseButton>
				<Inline space="space.100" alignBlock="center">
					{onGoBack && (
						<IconButton
							onClick={(_, analyticsEvent) => handleGoBack(analyticsEvent)}
							appearance="subtle"
							icon={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
							label={formatMessage(messages.goBackLabel)}
						/>
					)}
					<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>
					{isAutodevTemplateAgent(agentDetailsData) && (
						<Box paddingInlineStart="space.150">
							<Lozenge appearance="discovery">{formatMessage(messages.betaLabel)}</Lozenge>
						</Box>
					)}
				</Inline>
			</ModalHeader>
			<ModalBody>
				{!agentDetailsData.is_default ? (
					<Box xcss={styles.banner}>
						<AgentBanner
							agentId={agentDetailsData.id}
							agentNamedId={agentDetailsData.external_config_reference ?? agentDetailsData.named_id}
							agentIdentityAccountId={agentDetailsData.identity_account_id}
							height={96}
							creatorType={
								fg('jira_improve_agent_profile_for_a2a') ? agentDetailsData.creator_type : undefined
							}
						/>
					</Box>
				) : null}
				<Grid xcss={styles.modalBody} columnGap="space.300">
					{/* Left side panel */}

					<Stack space="space.100" alignBlock="start">
						{!agentDetailsData.is_default ? (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							<Box xcss={styles.avatar} style={{ marginTop: '-105px' }}>
								<AgentAvatar
									agentId={agentDetailsData.id}
									agentNamedId={
										agentDetailsData.external_config_reference ?? agentDetailsData.named_id
									}
									agentIdentityAccountId={agentDetailsData.identity_account_id}
									size="xxlarge"
									isForgeAgent={
										fg('rovo_agent_support_a2a_avatar')
											? isForgeAgentByCreatorType(agentDetailsData.creator_type as AgentCreatorType)
											: isForgeAgent(agentDetailsData)
									}
									forgeAgentIconUrl={agentDetailsData.icon}
								/>
							</Box>
						) : null}
						<AgentProfileInfo
							agentName={agentDetailsData.name}
							isStarred={agentDetailsData.favourite}
							onStarToggle={() => handleFavouriteAgent(agentDetailsData.id)}
							creatorRender={
								<AgentCreatorDataComponent
									agentRef={agentFragment ?? null}
									onCreatorLinkClick={() => handleClickCreatorLink()}
									siteId={assistanceServiceParams.siteId}
								/>
							}
							starCountRender={
								<AgentStarCount isLoading={false} starCount={agentDetailsData.favourite_count} />
							}
							headingRender={
								fg('rovo_agents_agent_verification') && (
									<AgentVerified
										agentRef={agentFragment ?? null}
										adjacentTextSize="headingMedium"
									/>
								)
							}
							agentDescription={agentDetailsData.description}
							isHidden={agentDetailsData.visibility === 'PRIVATE'}
							renderAdditionalContent={
								renderAgentProfileAdditionalContent
									? () =>
											renderAgentProfileAdditionalContent({
												agentAri,
												creatorType: agentDetailsData.creator_type,
												canEditAgent: agentFragment?.permissions?.canEdit,
												agentIsAssignableInJira:
													agentFragment?.jiraAssignability?.isAssignable ?? null,
											})
									: undefined
							}
						/>
						<Inline space="space.100">
							{!!renderAgentProfileCta &&
								renderAgentProfileCta({
									agentAri,
									agentName: agentDetailsData.name,
									creatorType: agentDetailsData.creator_type,
									canEditAgent: agentFragment?.permissions?.canEdit,
									agentIsAssignableInJira: agentFragment?.jiraAssignability?.isAssignable ?? null,
								})}
							<ChatToAgentButton
								onClick={(_, analyticsEvents) => handleClickChat(analyticsEvents)}
							/>
							<AgentDropdownMenu
								agentId={agentDetailsData.id}
								doesAgentHaveIdentityAccountId={!!agentDetailsData.identity_account_id}
								onEditAgent={() => onEditAgent(agentDetailsData.id)}
								onCopyAgent={() =>
									onCopyAgent({
										agentId: agentDetailsData.id,
										agentIdentityAccountId: agentDetailsData.identity_account_id,
									})
								}
								onDuplicateAgent={async () => {
									await onDuplicateAgent(agentDetailsData.id, agentAri);
								}}
								onDeleteAgent={() => onDeleteAgent(agentDetailsData, 'viewAgent')}
								isForgeAgent={isCurrentAgentForgeAgent}
								onViewAgentFullProfileClick={() => onViewAgentFullProfile(agentDetailsData.id)}
								assistanceServiceParams={assistanceServiceParams}
								agentRef={agentFragment ?? null}
								userPermissionsRef={userPermissionsFragment ?? null}
								shouldRenderToParent
							/>
						</Inline>
						<AgentAboutCard
							agent={agentDetailsData}
							assistanceServiceParams={assistanceServiceParams}
							scenarioListRef={scenarioListFragment ?? null}
						/>
					</Stack>

					{/* Right side panel */}

					<Stack space="space.400" alignBlock="start">
						<RightSection title={formatMessage(messages.openChatLabel)}>
							<Stack alignInline="start" space="space.100" alignBlock="start">
								<AgentConversationStartersWithAnalytics
									isAgentDefault={agentDetailsData.is_default}
									userDefinedConversationStarters={userDefinedConversationStarters}
									interactionSource="viewAgentConversationStarters"
									onConversationStarterClick={(conversationStarter) => {
										handleClickConversationStarter(agentDetailsData, conversationStarter);
										onClose();
									}}
								/>
							</Stack>
						</RightSection>
						{showBehaviourSection && (
							<RightSection title={formatMessage(messages.behaviourLabel)}>
								<AgentBehaviour
									agent={agentDetailsData}
									onCopy={handleCopy}
									isForgeAgent={isCurrentAgentForgeAgent}
								/>
							</RightSection>
						)}
						{(!hasCustomScenarios || isAgentV2) && (
							<RightSection title={formatMessage(messages.instructionsLabel)}>
								<AgentInstructions
									agent={agentDetailsData}
									instructions={agentInstructions}
									onCopy={handleCopy}
								/>
							</RightSection>
						)}

						{hasCustomScenarios && (
							<RightSection
								title={formatMessage(isAgentV2 ? messages.subagentsLabel : messages.scenariosLabel)}
							>
								<ScenariosSection
									scenarios={agentDetailsData.scenarios ?? []}
									assistanceServiceParams={assistanceServiceParams}
									cloudUrl={tenantData?.cloudUrl}
									scenarioListRef={scenarioListFragment ?? null}
									agentSchemaNumber={agentFragment?.agentSchemaNumber ?? 1}
								/>
							</RightSection>
						)}
					</Stack>
				</Grid>
			</ModalBody>
		</Fragment>
	);
};

type ViewAgentModalProps = Omit<
	ViewAgentModalInternalProps,
	'agentDetailsData' | 'agentRef' | 'scenarioListRef' | 'userPermissionsRef' | 'agentAri'
> & {
	agentId: string;
	wrapInModal?: boolean;
};

const ViewAgentModalRelay = (props: ViewAgentModalProps) => {
	di(useAgentDetailsController, logException);

	const {
		agentId,
		assistanceServiceParams: { siteId, headers: assistanceServiceHeaders },
	} = props;

	const { formatMessage } = useIntl();

	// TODO: this can be consolidated with the useLazyLoadQuery below https://product-fabric.atlassian.net/browse/RAGE-2291
	const { data: agentDetailsData, isFetching: agentDetailsLoading } = useAgentDetailsController({
		agentId,
		assistanceServiceParams: props.assistanceServiceParams,
	});

	const agentAnalyticsAttributes: {
		attributes: AIMateAgentAttributeType;
	} = useMemo(() => {
		if (!agentDetailsData) {
			// this code is not going to be used due to if condition below
			return {} as { attributes: AIMateAgentAttributeType };
		}

		return {
			attributes: {
				agentId: agentDetailsData.id,
				agentCreatorType: agentDetailsData.creator_type,
				agentVisibility: agentDetailsData.visibility ?? null,
				agentIsDefault: agentDetailsData.is_default,
				agentIsVerified: agentDetailsData.is_verified ?? null,
				agentExternalConfigReference: agentDetailsData.external_config_reference ?? null,
				agentActions: agentDetailsData.action_config?.map((config) => config.id) ?? [],
				experienceId: props.assistanceServiceParams.experienceId,
			},
		};
	}, [agentDetailsData, props.assistanceServiceParams.experienceId]);

	// TODO: Replace activation query + ARI construction below with useResolveAgentAri from controllers/agent-ari
	const activationResult = useLazyLoadQuery<viewAgent_activationQuery>(
		graphql`
			query viewAgent_activationQuery($cloudId: ID!, $product: String!) {
				tenantContexts(cloudIds: [$cloudId]) {
					activationIdByProduct(product: $product) {
						active
					}
				}
			}
		`,
		{ cloudId: siteId, product: 'rovo' },
	);

	const activationId = activationResult?.tenantContexts?.[0]?.activationIdByProduct?.active;
	const hasActivationId = Boolean(activationId);
	const agentAri = hasActivationId
		? `ari:cloud:rovo::agent/activation/${activationId}/${agentId}`
		: '';

	useEffect(() => {
		if (!hasActivationId) {
			logException({
				exception: new Error('viewAgent_activationQuery fail to get activationId'),
				name: 'viewAgent_activationQuery',
				tags: {
					siteId,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				},
			});
		}
	}, [hasActivationId, agentId, siteId]);

	const headers = new Headers(assistanceServiceHeaders);
	const maybeWorkspaceId = headers.get('x-workspace-id');

	const agentDetailsDataGql = useLazyLoadQuery<viewAgent_agentDetailsQuery>(
		graphql`
			query viewAgent_agentDetailsQuery(
				$agentAri: ID!
				$hasActivationId: Boolean!
				$cloudIdString: String!
				$cloudId: ID!
				$first: Int
				$agentId: ID!
				$product: String!
				$workspaceId: String
				$enableJiraAgentFields: Boolean!
				$enableStudioGSNAndScenarioRefresh: Boolean!
			) {
				agentStudio_agentById(id: $agentAri, product: $product, workspaceId: $workspaceId)
					@include(if: $hasActivationId)
					@optIn(to: "AgentStudio") {
					__typename
					...viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_agentRef
						@dangerously_unaliased_fixme
						@arguments(
							enableJiraAgentFields: $enableJiraAgentFields
							cloudId: $cloudId
							enableStudioGSNAndScenarioRefresh: $enableStudioGSNAndScenarioRefresh
						)
					... on QueryError {
						message
					}
				}
				agentStudio_scenarioListByContainerId(
					cloudId: $cloudIdString
					containerId: $agentId
					first: $first
				) @optIn(to: "AgentStudio") {
					# TODO https://product-fabric.atlassian.net/browse/RAGE-2291 replace tools and scenarios with this graphql query
					__typename
					...viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_scenarioListRef
				}
				atlassianStudio_userSiteContext(cloudId: $cloudId) {
					... on AtlassianStudioUserSiteContextOutput {
						userPermissions {
							# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
							...viewAgent_AtlassianConversationAssistantAgent_ViewAgentModalInternal_userPermissionsRef
						}
					}
				}
			}
		`,
		{
			agentAri,
			hasActivationId,
			cloudIdString: siteId,
			cloudId: siteId,
			first: 5,
			agentId: props.agentId,
			product: props.assistanceServiceParams.product,
			workspaceId: maybeWorkspaceId,
			enableJiraAgentFields: Boolean(props.enableJiraAgentFields),
			enableStudioGSNAndScenarioRefresh: fg('studio-gsn-and-scenario-refresh'),
		},
	);

	const scenarioListRef = agentDetailsDataGql?.agentStudio_scenarioListByContainerId ?? null;

	useEffect(() => {
		if (
			agentDetailsDataGql?.agentStudio_agentById?.__typename === 'QueryError' &&
			agentDetailsDataGql.agentStudio_agentById.message
		) {
			logException({
				exception: new Error(
					`viewAgent_agentDetailsQuery returns QueryError: ${agentDetailsDataGql.agentStudio_agentById.message}`,
				),
				name: 'viewAgent_agentDetailsQuery',
				tags: {
					siteId,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				},
			});
		}
	}, [
		agentDetailsDataGql?.agentStudio_agentById?.__typename,
		agentDetailsDataGql?.agentStudio_agentById?.message,
		siteId,
	]);

	if (agentDetailsLoading) {
		return <ViewAgentDetailSkeleton />;
	}

	if (!agentDetailsData) {
		return <div>{formatMessage(messages.agentNotFound)}</div>;
	}

	return (
		<AIMateAnalyticsContext data={agentAnalyticsAttributes}>
			<ViewAgentModalInternal
				{...props}
				agentAri={agentAri}
				agentDetailsData={agentDetailsData}
				agentRef={agentDetailsDataGql?.agentStudio_agentById ?? null}
				scenarioListRef={scenarioListRef}
				userPermissionsRef={
					agentDetailsDataGql?.atlassianStudio_userSiteContext?.userPermissions ?? null
				}
			/>
		</AIMateAnalyticsContext>
	);
};

export const ViewAgentModal = (props: ViewAgentModalProps): JSX.Element => {
	di(AtlassianRelayEnvironmentProvider);
	const { sendAnalyticsEvent } = useAnalytics();

	const handleCloseViewAgentModal = (analyticsEvent: UIAnalyticsEvent) => {
		props.onClose();
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentCloseModalButton',
				attributes: {
					agentId: props.agentId,
				},
			}),
		);
	};

	const modalBody = useMemo(
		() => (
			<AtlassianRelayEnvironmentProvider>
				<Suspense fallback={<ViewAgentDetailSkeleton />}>
					<ViewAgentModalRelay {...props} />
				</Suspense>
			</AtlassianRelayEnvironmentProvider>
		),
		[props],
	);

	return (
		<AnalyticsContextWithSourceAndProperties source="AIMate">
			{(props.wrapInModal ?? true) ? (
				<Modal
					testId="view-agent-modal"
					height="100vh"
					width="x-large"
					shouldCloseOnEscapePress
					onClose={(_, analyticsEvent) => handleCloseViewAgentModal(analyticsEvent)}
				>
					{modalBody}
				</Modal>
			) : (
				modalBody
			)}
		</AnalyticsContextWithSourceAndProperties>
	);
};
